import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Box, Pagination, usePagination, withStyles } from 'vopay';
import { SideDrawer, Skeleton, TransactionStatus } from '#shared/components/UI';
import useFetchClientAccounts from '#shared/hooks/useApi/useFetchClientAccounts';
import { useToast } from '#shared/providers/Toast';
import Table from './Table/Table';
import styles from './ClientAccountsDrawer.module.scss';

const cn = withStyles(styles);

const PAGE_SIZE = 10;

type ClientAccountsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The sub-account whose client accounts are listed, shown under the title. */
  accountName: string;
  /** Its JWT (the list row's Token), which lists its client accounts. */
  token: string;
};

// Read-only list of one sub-account's linked client accounts, opened from the list's Client Accounts count
// (ID-13661). Names aren't links: the client account page loads with the session's account, which can't see a
// sub-account's client accounts.
export default function ClientAccountsDrawer({ isOpen, onClose, accountName, token }: ClientAccountsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList' });
  const toast = useToast();
  const { clientAccounts, fetchingClientAccounts, fetchClientAccounts } = useFetchClientAccounts();

  // The endpoint adds the primary and billing client accounts to page 1 on top of Limit, and to page 1's
  // TotalRecords only, so the page count comes from the paged client accounts alone.
  const [numPagedClientAccounts, setNumPagedClientAccounts] = useState(0);

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(numPagedClientAccounts, {
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchClientAccounts({ Limit: PAGE_SIZE, Offset: offset, LinkedOnly: true }, token);

        if (offset === 0) {
          const numExtraOnFirstPage = Math.max(0, res.ClientAccounts.length - PAGE_SIZE);
          setNumPagedClientAccounts(res.PaginationMetaData.TotalRecords - numExtraOnFirstPage);
        }
      } catch (error) {
        // The hook already logged.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('ClientAccountsDrawer.loadError', {
            errorMessage,
            defaultValue: 'Could not load the client accounts: {{errorMessage}}',
          }),
        );
      }
    };

    fetchData();
  }, [offset, token]);

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClassName={cn('drawer')}
      title={
        <div className={cn('header')}>
          <span>{t('ClientAccountsDrawer.title', { defaultValue: 'Client Accounts' })}</span>

          <BodyText weight="md" className={cn('accountName')}>
            {accountName}
          </BodyText>
        </div>
      }
    >
      <div className={cn('content')}>
        {fetchingClientAccounts ? (
          <Skeleton height="320px" />
        ) : (
          <Table
            id="clientAccountsDrawerList"
            hasPlaceholderArrows={false}
            rows={clientAccounts.map((clientAccount) => ({
              id: clientAccount.ClientAccountID,
              data: clientAccount,
            }))}
            columns={[
              {
                id: 'clientAccountID',
                header: t('ClientAccountsDrawer.id', { defaultValue: 'ID' }),
                field: 'ClientAccountID',
                align: 'left',
              },
              {
                id: 'name',
                header: t('ClientAccountsDrawer.name', { defaultValue: 'Name' }),
                field: 'ClientName',
                align: 'left',
              },
              {
                id: 'status',
                header: t('Attributes.status', { defaultValue: 'Status' }),
                align: 'left',
                template: ({ data }) =>
                  data.IsActive ? (
                    <TransactionStatus
                      status="active"
                      label={t('Attributes.activeBadge', { defaultValue: 'Active' })}
                    />
                  ) : (
                    <TransactionStatus
                      status="not active"
                      label={t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })}
                    />
                  ),
              },
              {
                id: 'email',
                header: t('ClientAccountsDrawer.email', { defaultValue: 'Email' }),
                field: 'EmailAddress',
                align: 'left',
              },
              {
                id: 'type',
                header: t('ClientAccountsDrawer.type', { defaultValue: 'Client Account Type' }),
                align: 'left',
                template: ({ data }) =>
                  data.IsBusiness
                    ? t('ClientAccountsDrawer.business', { defaultValue: 'Business' })
                    : t('ClientAccountsDrawer.individual', { defaultValue: 'Individual' }),
              },
            ]}
          />
        )}

        {pageCount > 1 && (
          <Box flx="row" jc="center">
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => setPageIndex(e.selected)}
            />
          </Box>
        )}
      </div>
    </SideDrawer>
  );
}
